import React, { type Ref } from 'react';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Space } from 'antd';
import type { SizeType } from 'antd/es/config-provider/SizeContext';
import { useTranslation } from 'react-i18next';
import type { NameCheckStatus } from '@hooks/orders/useOrderNameAvailability';
import type { OrderRole } from './types';

interface OrderNameFieldProps {
  role: OrderRole;
  status: NameCheckStatus;
  existingOrderId: string | null;
  onChange: (value: string) => void;
  /** When omitted, the suggest button is not rendered. */
  onSuggest?: () => void;
  suggesting?: boolean;
  /** Disables the suggest button (e.g. while the parent is submitting). */
  disabled?: boolean;
  size?: SizeType;
  /** Wraps the Form.Item in a div with this ref — used for the new-order Tour anchor. */
  anchorRef?: Ref<HTMLDivElement>;
  testId?: string;
}

function statusSuffix(status: NameCheckStatus): React.ReactNode {
  switch (status) {
    case 'checking':  return <LoadingOutlined style={{ color: '#1890ff' }} />;
    case 'available': return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    case 'unavailable': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    default: return <span />;
  }
}

const OrderNameField: React.FC<OrderNameFieldProps> = ({
  role,
  status,
  existingOrderId,
  onChange,
  onSuggest,
  suggesting = false,
  disabled = false,
  size,
  anchorRef,
  testId,
}) => {
  const { t } = useTranslation();

  const help = status === 'unavailable' ? (
    <div style={{ color: '#ff4d4f' }}>
      {t('shared.orders.form.messages.order_name_exists')}
      {existingOrderId && (
        <a
          href={`/${role}/orders/${existingOrderId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: 8 }}
        >
          {t('shared.orders.form.messages.view_existing_order')}
        </a>
      )}
    </div>
  ) : null;

  const field = (
    <Form.Item
      label={t('models.order.name')}
      required
      validateStatus={status === 'unavailable' ? 'error' : ''}
      help={help}
    >
      <Space.Compact style={{ width: '100%' }}>
        <Form.Item
          name="name"
          noStyle
          rules={[{ required: true, message: t('common.validation.required') as string }]}
        >
          <Input
            data-testid={testId}
            size={size}
            placeholder={t('models.order.name') as string}
            onChange={(e) => onChange(e.target.value)}
            suffix={statusSuffix(status)}
          />
        </Form.Item>
        {onSuggest && (
          <Button
            size={size}
            onClick={onSuggest}
            loading={suggesting}
            disabled={disabled}
          >
            {t('shared.orders.form.suggest_name')}
          </Button>
        )}
      </Space.Compact>
    </Form.Item>
  );

  if (anchorRef) {
    return <div ref={anchorRef} style={{ width: '100%' }}>{field}</div>;
  }
  return field;
};

export default OrderNameField;
